import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Input, message, Checkbox, Modal, Form, InputNumber, Select, theme, Row, Col, Grid, Tooltip, Tag } from 'antd';
import { ReloadOutlined, EditOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../../components/common/StandardTable';
import { useGetQaqcPipeServiceclass, useGetPipeMaterialTypes, useGetQaqcPipeWeldingRod } from '../../../../hooks/genericHooks';
import type { QaqcPipeServiceClass, QaqcPipeMaterialTypes } from '../../../../types';
import { updateData, updateDataBatch } from '../../../../services/api';
import Button from '../../../../components/common/Button';

const { Title, Text } = Typography;

const readOnlyKeys: Array<keyof QaqcPipeServiceClass> = ['id','project_id','serviceclass_name','fluid'];

function QaqcPipeServiceClassEdit() {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { data, isLoading, isError, refetch } = useGetQaqcPipeServiceclass();
  const { data: matTypes } = useGetPipeMaterialTypes();
  const { data: weldingRodRows } = useGetQaqcPipeWeldingRod();
  const [serviceClassFilter, setServiceClassFilter] = useState<string | undefined>(undefined);
  const [fluidFilter, setFluidFilter] = useState<string | undefined>(undefined);
  const [materialCategoryFilter, setMaterialCategoryFilter] = useState<number | undefined>(undefined);
  const [heatNoFilter, setHeatNoFilter] = useState<'true' | 'false' | undefined>(undefined);
  const [bcRateFilter, setBcRateFilter] = useState<number | undefined>(undefined);
  const [fwRateFilter, setFwRateFilter] = useState<number | undefined>(undefined);
  const [flwRateFilter, setFlwRateFilter] = useState<number | undefined>(undefined);
  const [vcRateFilter, setVcRateFilter] = useState<number | undefined>(undefined);
  const rows = (data?.data ?? []) as QaqcPipeServiceClass[];
  // helper to coerce numeric-like values (including strings) to number
  const toNum = React.useCallback((v: any): number | undefined => {
    if (v === null || v === undefined || v === '') return undefined;
    if (typeof v === 'number') return isNaN(v) ? undefined : v;
    const n = Number(String(v).replace(/,/g, '.').replace(/[^0-9.+-]/g, ''));
    return isNaN(n) ? undefined : n;
  }, []);
  // Build a lookup for material type definitions by numeric category
  const matTypeMap = useMemo(() => {
    const map = new Map<number, QaqcPipeMaterialTypes>();
    const list = (matTypes?.data ?? []) as QaqcPipeMaterialTypes[];
    list.forEach((m) => {
      if (m && typeof m.category === 'number') map.set(m.category, m);
    });
    return map;
  }, [matTypes]);

  const materialTypeOptions = useMemo(() => {
    const list = (matTypes?.data ?? []) as QaqcPipeMaterialTypes[];
    return list.map((m) => {
      const name = m?.name ?? '';
      const desc = m?.description ? ` — ${m.description}` : '';
      const catPrefix = `${m.category} - `;
      return { label: `${catPrefix}${name}${desc}`.trim(), value: m.category };
    });
  }, [matTypes]);

  const weldingMethodOptions = useMemo(() => {
    const s = new Set<string>();
    ((weldingRodRows?.data ?? []) as any[]).forEach(r => { if (r.welding_method) s.add(r.welding_method); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [weldingRodRows]);

  const weldingRodOptions = useMemo(() => {
    const s = new Set<string>();
    ((weldingRodRows?.data ?? []) as any[]).forEach(r => { if (r.welding_rod) s.add(r.welding_rod); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [weldingRodRows]);

  const getRodOptions = React.useCallback((method?: string) => {
    const list = ((weldingRodRows?.data ?? []) as any[])
      .filter(r => !method || r.welding_method === method)
      .map((r: any) => r.welding_rod)
      .filter(Boolean);
    const uniq = Array.from(new Set(list as string[])).sort();
    return uniq.map(v => ({ label: v, value: v }));
  }, [weldingRodRows]);

  const serviceClassOptions = useMemo(() => {
    const s = new Set<string>();
    rows
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .forEach(r => { if (r.serviceclass_name) s.add(r.serviceclass_name); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [rows, fluidFilter, materialCategoryFilter, heatNoFilter]);

  const fluidOptions = useMemo(() => {
    const s = new Set<string>();
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .forEach(r => { if (r.fluid) s.add(r.fluid); });
    return Array.from(s).sort().map(v => ({ label: v, value: v }));
  }, [rows, serviceClassFilter, materialCategoryFilter, heatNoFilter]);

  const materialCategoryOptions = useMemo(() => {
    const list = (matTypes?.data ?? []) as QaqcPipeMaterialTypes[];
    return list
      .slice()
      .sort((a, b) => a.category - b.category)
      .map((m) => {
        const name = m?.name ? ` - ${m.name}` : '';
        const desc = m?.description ? ` — ${m.description}` : '';
        return { label: `${m.category}${name}${desc}`.trim(), value: m.category };
      });
  }, [matTypes]);

  const heatNoOptions = useMemo(() => {
    let hasTrue = false;
    let hasFalse = false;
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .forEach(r => {
        const v = !!(r as any).heat_no_use;
        if (v) hasTrue = true; else hasFalse = true;
      });
    const opts: Array<{label: string; value: 'true' | 'false'}> = [];
    if (hasTrue) opts.push({ label: 'True', value: 'true' });
    if (hasFalse) opts.push({ label: 'False', value: 'false' });
    return opts;
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter]);

  // Cascading options for BC/FW/FLW/VC based on all other current selections
  const bcRateOptions = useMemo(() => {
    const s = new Set<number>();
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .filter(r => fwRateFilter == null || r.fw_rate === fwRateFilter)
      .filter(r => flwRateFilter == null || r.flw_rate === flwRateFilter)
      .filter(r => vcRateFilter == null || r.vc_rate === vcRateFilter)
      .forEach(r => { const v = toNum((r as any).bc_rate); if (v !== undefined) s.add(v); });
    return Array.from(s).sort((a,b)=>a-b).map(v => ({ label: String(v), value: v }));
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter, heatNoFilter, fwRateFilter, flwRateFilter, vcRateFilter, toNum]);

  const fwRateOptions = useMemo(() => {
    const s = new Set<number>();
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .filter(r => bcRateFilter == null || r.bc_rate === bcRateFilter)
      .filter(r => flwRateFilter == null || r.flw_rate === flwRateFilter)
      .filter(r => vcRateFilter == null || r.vc_rate === vcRateFilter)
      .forEach(r => { const v = toNum((r as any).fw_rate); if (v !== undefined) s.add(v); });
    return Array.from(s).sort((a,b)=>a-b).map(v => ({ label: String(v), value: v }));
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter, heatNoFilter, bcRateFilter, flwRateFilter, vcRateFilter, toNum]);

  const flwRateOptions = useMemo(() => {
    const s = new Set<number>();
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .filter(r => bcRateFilter == null || r.bc_rate === bcRateFilter)
      .filter(r => fwRateFilter == null || r.fw_rate === fwRateFilter)
      .filter(r => vcRateFilter == null || r.vc_rate === vcRateFilter)
      .forEach(r => { const v = toNum((r as any).flw_rate); if (v !== undefined) s.add(v); });
    return Array.from(s).sort((a,b)=>a-b).map(v => ({ label: String(v), value: v }));
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter, heatNoFilter, bcRateFilter, fwRateFilter, vcRateFilter, toNum]);

  const vcRateOptions = useMemo(() => {
    const s = new Set<number>();
    rows
      .filter(r => !serviceClassFilter || r.serviceclass_name === serviceClassFilter)
      .filter(r => !fluidFilter || r.fluid === fluidFilter)
      .filter(r => materialCategoryFilter == null || r.material_category === materialCategoryFilter)
      .filter(r => !heatNoFilter || (heatNoFilter === 'true' ? !!(r as any).heat_no_use : !(r as any).heat_no_use))
      .filter(r => bcRateFilter == null || r.bc_rate === bcRateFilter)
      .filter(r => fwRateFilter == null || r.fw_rate === fwRateFilter)
      .filter(r => flwRateFilter == null || r.flw_rate === flwRateFilter)
      .forEach(r => { const v = toNum((r as any).vc_rate); if (v !== undefined) s.add(v); });
    return Array.from(s).sort((a,b)=>a-b).map(v => ({ label: String(v), value: v }));
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter, heatNoFilter, bcRateFilter, fwRateFilter, flwRateFilter, toNum]);

  // Reset selections if they become invalid based on current options
  useEffect(() => {
    if (serviceClassFilter && !serviceClassOptions.some(o => o.value === serviceClassFilter)) {
      setServiceClassFilter(undefined);
    }
  }, [serviceClassOptions, serviceClassFilter]);

  useEffect(() => {
    if (fluidFilter && !fluidOptions.some(o => o.value === fluidFilter)) {
      setFluidFilter(undefined);
    }
  }, [fluidOptions, fluidFilter]);

  useEffect(() => {
    if (materialCategoryFilter != null && !materialCategoryOptions.some(o => o.value === materialCategoryFilter)) {
      setMaterialCategoryFilter(undefined);
    }
  }, [materialCategoryOptions, materialCategoryFilter]);

  useEffect(() => {
    if (heatNoFilter && !heatNoOptions.some(o => o.value === heatNoFilter)) {
      setHeatNoFilter(undefined);
    }
  }, [heatNoOptions, heatNoFilter]);

  useEffect(() => { if (bcRateFilter != null && !bcRateOptions.some(o => o.value === bcRateFilter)) setBcRateFilter(undefined); }, [bcRateOptions, bcRateFilter]);
  useEffect(() => { if (fwRateFilter != null && !fwRateOptions.some(o => o.value === fwRateFilter)) setFwRateFilter(undefined); }, [fwRateOptions, fwRateFilter]);
  useEffect(() => { if (flwRateFilter != null && !flwRateOptions.some(o => o.value === flwRateFilter)) setFlwRateFilter(undefined); }, [flwRateOptions, flwRateFilter]);
  useEffect(() => { if (vcRateFilter != null && !vcRateOptions.some(o => o.value === vcRateFilter)) setVcRateFilter(undefined); }, [vcRateOptions, vcRateFilter]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const matchesServiceClass = !serviceClassFilter || r.serviceclass_name === serviceClassFilter;
      const matchesFluid = !fluidFilter || r.fluid === fluidFilter;
      const matchesMatCat = materialCategoryFilter == null || r.material_category === materialCategoryFilter;
      const heatVal = !!(r as any).heat_no_use;
      const matchesHeat = !heatNoFilter || (heatNoFilter === 'true' ? heatVal === true : heatVal === false);
      const matchesBc = bcRateFilter == null || toNum((r as any).bc_rate) === bcRateFilter;
      const matchesFw = fwRateFilter == null || toNum((r as any).fw_rate) === fwRateFilter;
      const matchesFlw = flwRateFilter == null || toNum((r as any).flw_rate) === flwRateFilter;
      const matchesVc = vcRateFilter == null || toNum((r as any).vc_rate) === vcRateFilter;
      return matchesServiceClass && matchesFluid && matchesMatCat && matchesHeat && matchesBc && matchesFw && matchesFlw && matchesVc;
    });
  }, [rows, serviceClassFilter, fluidFilter, materialCategoryFilter, heatNoFilter, bcRateFilter, fwRateFilter, flwRateFilter, vcRateFilter, toNum]);

  const headerWithInfo = React.useCallback((label: string, info: string) => (
    <span>
      {label}
      <Tooltip title={info}>
        <QuestionCircleOutlined style={{ color: '#999', marginLeft: 4 }} />
      </Tooltip>
    </span>
  ), []);

  // Modal edit state (no inline updates)
  const [editing, setEditing] = useState<QaqcPipeServiceClass | null>(null);
  const [form] = Form.useForm<QaqcPipeServiceClass>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchMode, setBatchMode] = useState(false);

  const openEdit = (row: QaqcPipeServiceClass) => {
    setBatchMode(false);
    setEditing(row);
  };

  useEffect(() => {
    if (!editing) return;
    // Normalize incoming values for proper control rendering (runs after modal mounts)
    const numericKeys = [
      'bc_rate','fw_rate','flw_rate','vc_rate','pmi_rate',
      'pt_bw_rate','pt_fw_rate','pt_sp_rate',
      'rt_bw_rate','rt_fw_rate','rt_sp_rate',
      'mt_bw_rate','mt_fw_rate','mt_sp_rate',
      'ut_bw_rate','ut_fw_rate','ut_sp_rate',
      'pwht_bw_hardness','pwht_fw_hardness','pwht_sp_hardness',
    ] as const;
    const normalized: any = { ...editing };
    const toNumber = (val: any): number | undefined => {
      if (val === null || val === undefined || val === '') return undefined;
      if (typeof val === 'number') return isNaN(val) ? undefined : val;
      if (typeof val === 'string') {
        const withDot = val.replace(/,/g, '.');
        const cleaned = withDot.replace(/[^0-9.+-]/g, '').trim();
        const n = parseFloat(cleaned);
        return isNaN(n) ? undefined : n;
      }
      const n = Number(val);
      return isNaN(n) ? undefined : n;
    };
    numericKeys.forEach((k) => {
      const v = (editing as any)[k];
      normalized[k] = toNumber(v);
    });
    // Ensure the five NDT fields show a value; default to 0 if undefined
    ['bc_rate','fw_rate','flw_rate','vc_rate','pmi_rate'].forEach((k) => {
      if (normalized[k] === undefined) normalized[k] = 0;
    });
    if (typeof (editing as any).heat_no_use !== 'boolean') {
      normalized.heat_no_use = !!(editing as any).heat_no_use;
    }
    form.resetFields();
    form.setFieldsValue(normalized);
  }, [editing, form]);

  // When entering batch mode, reset form to blank so only changed fields will be sent
  useEffect(() => {
    if (!batchMode) return;
    form.resetFields();
  }, [batchMode, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const targetIds: number[] = batchMode ? (selectedRowKeys as number[]) : [editing!.id];
      const payload: Partial<QaqcPipeServiceClass> = {};
      Object.keys(values).forEach((k) => {
        const key = k as keyof QaqcPipeServiceClass;
        if (!readOnlyKeys.includes(key)) (payload as any)[key] = (values as any)[k];
      });
      // For batch updates, only include fields that are not undefined to avoid overwriting unintentionally
      Object.keys(payload).forEach((k) => {
        const v = (payload as any)[k];
        if (v === undefined) delete (payload as any)[k];
      });
      if (batchMode) {
        const resp = await updateDataBatch('qaqc_pipe_serviceclass', { id_field: 'id', ids: targetIds, data: payload });
        const updated = resp?.updated ?? 0;
        const failed = resp?.failed ?? 0;
        message.success(`Updated ${updated} of ${targetIds.length} selected record(s)` + (failed ? `, ${failed} failed` : ''));
      } else {
        await updateData('qaqc_pipe_serviceclass', targetIds[0], payload, 'id');
        message.success('Saved');
      }
      setEditing(null);
      setBatchMode(false);
      form.resetFields();
      setSelectedRowKeys([]);
      refetch();
    } catch (e: any) {
      if (e?.errorFields) return; // validation error
      message.error(e?.response?.data?.error || 'Save failed');
    }
  };
  const defaultwidth = 60
  const columns: ColumnsType<QaqcPipeServiceClass> = [
    {
      title: headerWithInfo('General', 'Basic identifiers'), key: 'general_info', children: [
        { title: 'Serv.Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 110, ellipsis: true },
        { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 90, ellipsis: true },
        { title: 'Mat.', dataIndex: 'material_category', key: 'material_category', width: 80 },
      ]
    },
    {
      title: headerWithInfo('INSPECTION / NDT Rates', 'Non-Destructive Testing Rates (%)'), key: 'ndt_rates', children: [
        { title: 'BC', dataIndex: 'bc_rate', key: 'bc_rate', width: defaultwidth, align: 'left' },
        { title: 'FW', dataIndex: 'fw_rate', key: 'fw_rate', width: defaultwidth, align: 'center'},
        { title: 'FLW', dataIndex: 'flw_rate', key: 'flw_rate', width: defaultwidth, align: 'center' },
        { title: 'VC', dataIndex: 'vc_rate', key: 'vc_rate', width: defaultwidth, align: 'center' },
        { title: 'PMI', dataIndex: 'pmi_rate', key: 'pmi_rate', width: defaultwidth, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('PT %', 'Penetrant Testing (%)'), key: 'pt_rates', children: [
        { title: 'BW', dataIndex: 'pt_bw_rate', key: 'pt_bw_rate', width: defaultwidth, align: 'center' },
        { title: 'FW', dataIndex: 'pt_fw_rate', key: 'pt_fw_rate', width: defaultwidth, align: 'center' },
        { title: 'SP', dataIndex: 'pt_sp_rate', key: 'pt_sp_rate', width: defaultwidth, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('RT %', 'Radiographic Testing (%)'), key: 'rt_rates', children: [
        { title: 'BW', dataIndex: 'rt_bw_rate', key: 'rt_bw_rate', width: defaultwidth, align: 'center' },
        { title: 'FW', dataIndex: 'rt_fw_rate', key: 'rt_fw_rate', width: defaultwidth, align: 'center' },
        { title: 'SP', dataIndex: 'rt_sp_rate', key: 'rt_sp_rate', width: defaultwidth, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('MT %', 'Magnetic Particle Testing (%)'), key: 'mt_rates', children: [
        { title: 'BW', dataIndex: 'mt_bw_rate', key: 'mt_bw_rate', width: defaultwidth, align: 'center' },
        { title: 'FW', dataIndex: 'mt_fw_rate', key: 'mt_fw_rate', width: defaultwidth, align: 'center' },
        { title: 'SP', dataIndex: 'mt_sp_rate', key: 'mt_sp_rate', width: defaultwidth, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('UT %', 'Ultrasonic Testing (%)'), key: 'ut_rates', children: [
        { title: 'BW', dataIndex: 'ut_bw_rate', key: 'ut_bw_rate', width: defaultwidth, align: 'center' },
        { title: 'FW', dataIndex: 'ut_fw_rate', key: 'ut_fw_rate', width: defaultwidth, align: 'center' },
        { title: 'SP', dataIndex: 'ut_sp_rate', key: 'ut_sp_rate', width: defaultwidth, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('PWHT Hardness', 'Post Weld Heat Treatment Hardness'), key: 'pwht', children: [
        { title: 'BW', dataIndex: 'pwht_bw_hardness', key: 'pwht_bw_hardness', width: 80, align: 'center' },
        { title: 'FW', dataIndex: 'pwht_fw_hardness', key: 'pwht_fw_hardness', width: 80, align: 'center' },
        { title: 'SP', dataIndex: 'pwht_sp_hardness', key: 'pwht_sp_hardness', width: 80, align: 'center' },
      ]
    },
    {
      title: headerWithInfo('Heat No. Use', 'Whether heat number is required'), dataIndex: 'heat_no_use', key: 'heat_no_use', width: 60,
      render: (v: any) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>
    },
    {
      title: headerWithInfo('Welding Consumables', 'Method and rod selections'), key: 'consumables', children: [
        { title: 'BW 1st', dataIndex: 'buttweld_1st', key: 'buttweld_1st', width: 100},
        { title: 'BW>Small', dataIndex: 'buttweld_over_small', key: 'buttweld_over_small', width: 100 },
        { title: 'BW>Large', dataIndex: 'buttweld_over_large', key: 'buttweld_over_large', width: 100 },
        { title: 'BW 1stRod', dataIndex: 'buttweld_1st_rod', key: 'buttweld_1st_rod', width: 100 },
        { title: 'BW>S.Rod', dataIndex: 'buttweld_over_small_rod', key: 'buttweld_over_small_rod', width: 100 },
        { title: 'BW>L.Rod', dataIndex: 'buttweld_over_large_rod', key: 'buttweld_over_large_rod', width: 100 },
        { title: 'FW 1st', dataIndex: 'filetweld_1st', key: 'filetweld_1st', width: 100 },
        { title: 'FW>Small', dataIndex: 'filetweld_over_small', key: 'filetweld_over_small', width: 100 },
        { title: 'FW>Large', dataIndex: 'filetweld_over_large', key: 'filetweld_over_large', width: 100 },
        { title: 'FW 1stRod', dataIndex: 'filetweld_1st_rod', key: 'filetweld_1st_rod', width: 100 },
        { title: 'FW>S.Rod', dataIndex: 'filetweld_over_small_rod', key: 'filetweld_over_small_rod', width: 100 },
        { title: 'FW>L.Rod', dataIndex: 'filetweld_over_large_rod', key: 'filetweld_over_large_rod', width: 100 },
        { title: 'SP 1st', dataIndex: 'special_1st', key: 'special_1st', width: 100 },
        { title: 'SP>Small', dataIndex: 'special_over_small', key: 'special_over_small', width: 100 },
        { title: 'SP>Large', dataIndex: 'special_over_large', key: 'special_over_large', width: 100 },
        { title: 'SP 1stRod', dataIndex: 'special_1st_rod', key: 'special_1st_rod', width: 100 },
        { title: 'SP>S.Rod', dataIndex: 'special_over_small_rod', key: 'special_over_small_rod', width: 100 },
        { title: 'SP>L.Rod', dataIndex: 'special_over_large_rod', key: 'special_over_large_rod', width: 100 },
      ]
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={4}>
          <Title level={4} style={{ margin: 0 }}>Service Classes</Title>
        </Col>
        <Col xs={24} md={16}>
      <div style={{ overflowX: screens.lg ? 'visible' : 'auto', whiteSpace: screens.lg ? 'normal' : 'nowrap' }}>
      <Form layout="vertical" style={{ display: 'inline-flex', flexWrap: screens.lg ? 'wrap' : 'nowrap', gap: 8, alignItems: 'flex-end' }}>
            <Form.Item label="Service Class" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '12ch' }}
                placeholder={undefined}
                options={serviceClassOptions}
                value={serviceClassFilter}
                onChange={setServiceClassFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="Fluid" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '10ch' }}
                placeholder={undefined}
                options={fluidOptions}
                value={fluidFilter}
                onChange={setFluidFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="Material Category" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '27ch' }}
                placeholder={undefined}
                options={materialCategoryOptions}
                value={materialCategoryFilter}
                onChange={setMaterialCategoryFilter}
                allowClear
                showSearch
                optionFilterProp="value"
                size="small"
              />
            </Form.Item>
            <Form.Item label="Heat No.Use" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '10ch' }}
                placeholder={undefined}
                options={heatNoOptions}
                value={heatNoFilter}
                onChange={setHeatNoFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="BC %" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '8ch' }}
                placeholder={undefined}
                options={bcRateOptions}
                value={bcRateFilter}
                onChange={setBcRateFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="FW %" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '8ch' }}
                placeholder={undefined}
                options={fwRateOptions}
                value={fwRateFilter}
                onChange={setFwRateFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="FLW %" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '8ch' }}
                placeholder={undefined}
                options={flwRateOptions}
                value={flwRateFilter}
                onChange={setFlwRateFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
            <Form.Item label="VC %" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '8ch' }}
                placeholder={undefined}
                options={vcRateOptions}
                value={vcRateFilter}
                onChange={setVcRateFilter}
                allowClear
                showSearch
                optionFilterProp="label"
                size="small"
              />
            </Form.Item>
          </Form>
      </div>
        </Col>
        <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
          <Button
            action="edit"
            icon={<EditOutlined />}
            onClick={() => {
              if (!selectedRowKeys.length) { message.warning('Please select rows to edit'); return; }
              setEditing(null);
              setBatchMode(true);
            }}
            disabled={!selectedRowKeys.length}
          >
            multi_edit
          </Button>
        </Col>
      </Row>

      <StandardTable
        loading={isLoading}
        dataSource={filtered.map((r) => ({ key: r.id, ...r }))}
        columns={columns}
        rowKey="key"
        bordered
        scroll={{ x: 'max-content' }}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys, columnWidth: 8 }}
        onRow={(record: any) => ({ onDoubleClick: () => openEdit(record as QaqcPipeServiceClass) })}
      />

      <Modal
        title={<span style={{ color: token.colorPrimary }}>{batchMode ? `Batch Edit (${selectedRowKeys.length})` : 'Edit Service Class'}</span>}
        open={!!editing || batchMode}
        onCancel={() => { setEditing(null); setBatchMode(false); form.resetFields(); }}
        onOk={handleSave}
        okText="Save"
        destroyOnClose
        forceRender
        width={screens.lg ? 900 : (screens.md ? 720 : 360)}
        bodyStyle={{ overflowX: 'hidden' }}
      >
        {batchMode && (
          <Text type="secondary">Selected rows: {selectedRowKeys.length}</Text>
        )}
        <Form key={editing?.id} form={form} layout="vertical" size="small">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8}}>
            <Form.Item label="ID" name="id" style={{ marginBottom: 6 }}>
              <InputNumber disabled style={{ width: 80 }} />
            </Form.Item>
            <Form.Item label="Service Class" name="serviceclass_name" style={{ marginBottom: 6 }}>
              <Input disabled style={{ width: 80 }} />
            </Form.Item>
            <Form.Item label="Fluid" name="fluid" style={{ marginBottom: 6 }}>
              <Input disabled style={{ width: 80 }} />
            </Form.Item>
            <Form.Item label="Material Category" name="material_category" style={{ marginBottom: 6 }}>
              <Select
                options={materialTypeOptions}
                placeholder="Select material type"
                allowClear
                showSearch
                optionFilterProp="value"
                size="small"
                style={{ width: 100 }}
              />
            </Form.Item >
            <Form.Item label="Heat No. Use" name="heat_no_use" valuePropName="checked" style={{ marginBottom: 6 }}>
                <Checkbox />
            </Form.Item>
          </div>

          <Title level={5} style={{ marginTop: 8, marginBottom: 8, color: token.colorPrimary }}>NDT Rates (%)</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(174px, 1fr))', gap: 8 }}>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>BC</span>
                <Form.Item name="bc_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>FW</span>
                <Form.Item name="fw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>FLW</span>
                <Form.Item name="flw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>VC</span>
                <Form.Item name="vc_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>PMI</span>
                <Form.Item name="pmi_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
          </div>

          <Title level={5} style={{ marginTop: 8, marginBottom: 8, color: token.colorPrimary }}>PT / RT / MT / UT (%)</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>PT BW</span>
                <Form.Item name="pt_bw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>PT FW</span>
                <Form.Item name="pt_fw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>PT SP</span>
                <Form.Item name="pt_sp_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>RT BW</span>
                <Form.Item name="rt_bw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>RT FW</span>
                <Form.Item name="rt_fw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>RT SP</span>
                <Form.Item name="rt_sp_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>MT BW</span>
                <Form.Item name="mt_bw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>MT FW</span>
                <Form.Item name="mt_fw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>MT SP</span>
                <Form.Item name="mt_sp_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>UT BW</span>
                <Form.Item name="ut_bw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>UT FW</span>
                <Form.Item name="ut_fw_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span>UT SP</span>
                <Form.Item name="ut_sp_rate" noStyle>
                  <InputNumber style={{ width: 60 }} min={0} max={100} />
                </Form.Item>
              </Space>
            </Form.Item>
          </div>

          <Title level={5} style={{ marginTop: 8, marginBottom: 8, color: token.colorPrimary }}>PWHT Hardness</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>PWHT BW</span>
                <Form.Item name="pwht_bw_hardness" noStyle>
                  <InputNumber style={{ width: 80 }} min={0} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>PWHT FW</span>
                <Form.Item name="pwht_fw_hardness" noStyle>
                  <InputNumber style={{ width: 80 }} min={0} />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>PWHT SP</span>
                <Form.Item name="pwht_sp_hardness" noStyle>
                  <InputNumber style={{ width: 80 }} min={0} />
                </Form.Item>
              </Space>
            </Form.Item>
          </div>

          <Title level={5} style={{ marginTop: 8, marginBottom: 8, color: token.colorPrimary }}>Welding Consumables</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW 1st</span>
                <Form.Item name="buttweld_1st" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW {'>'} Small</span>
                <Form.Item name="buttweld_over_small" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW {'>'} Large</span>
                <Form.Item name="buttweld_over_large" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>

            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW 1st Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.buttweld_1st !== curr.buttweld_1st}>
                  {({ getFieldValue }) => (
                    <Form.Item name="buttweld_1st_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('buttweld_1st'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>

            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW {'>'} Small Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.buttweld_over_small !== curr.buttweld_over_small}>
                  {({ getFieldValue }) => (
                    <Form.Item name="buttweld_over_small_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('buttweld_over_small'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>BW {'>'} Large Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.buttweld_over_large !== curr.buttweld_over_large}>
                  {({ getFieldValue }) => (
                    <Form.Item name="buttweld_over_large_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('buttweld_over_large'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW 1st</span>
                <Form.Item name="filetweld_1st" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW {'>'} Small</span>
                <Form.Item name="filetweld_over_small" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW {'>'} Large</span>
                <Form.Item name="filetweld_over_large" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW 1st Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.filetweld_1st !== curr.filetweld_1st}>
                  {({ getFieldValue }) => (
                    <Form.Item name="filetweld_1st_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('filetweld_1st'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW {'>'} Small Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.filetweld_over_small !== curr.filetweld_over_small}>
                  {({ getFieldValue }) => (
                    <Form.Item name="filetweld_over_small_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('filetweld_over_small'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>FW {'>'} Large Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.filetweld_over_large !== curr.filetweld_over_large}>
                  {({ getFieldValue }) => (
                    <Form.Item name="filetweld_over_large_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('filetweld_over_large'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP 1st</span>
                <Form.Item name="special_1st" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP {'>'} Small</span>
                <Form.Item name="special_over_small" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP {'>'} Large</span>
                <Form.Item name="special_over_large" noStyle>
                  <Select style={{ width: 120 }} size="small" options={weldingMethodOptions} allowClear showSearch optionFilterProp="label" />
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP 1st Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.special_1st !== curr.special_1st}>
                  {({ getFieldValue }) => (
                    <Form.Item name="special_1st_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('special_1st'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP {'>'} Small Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.special_over_small !== curr.special_over_small}>
                  {({ getFieldValue }) => (
                    <Form.Item name="special_over_small_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('special_over_small'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
            <Form.Item style={{ marginBottom: 6 }}>
              <Space>
                <span style={{ display: 'inline-block', width: 110, textAlign: 'right' }}>SP {'>'} Large Rod</span>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.special_over_large !== curr.special_over_large}>
                  {({ getFieldValue }) => (
                    <Form.Item name="special_over_large_rod" noStyle>
                      <Select style={{ width: 120 }} size="small" options={getRodOptions(getFieldValue('special_over_large'))} allowClear showSearch optionFilterProp="label" />
                    </Form.Item>
                  )}
                </Form.Item>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Card>
  );
}

export default QaqcPipeServiceClassEdit;
